// PLACEMENT for the Scheduler calendar — turning the health projection into per-day entries.
//
// Two kinds of entry land on a day and they are NOT the same thing, so they never render alike: a
// SERVER-PROJECTED occurrence (something the scheduler module says is due — G-110) and a RECORDED Run
// Record (something that actually happened, with the failed/interrupted-vs-completed distinction the
// module recorded, G-107/G-111). The client only PLACES them on the local day their own timestamp names;
// it computes no recurrence and invents no entry, so a day the projection is silent about has none.
//
// Split out of RoutineCalendar.tsx so the month card and the day agenda can share one placement pass.

import type { ScheduleHealthScheduleRow } from '../projections.ts';
import {
  fmtClock,
  runLocalDate,
  runMarker,
  scheduleProjectionPresentation,
} from './scheduler-format.ts';

/** One placed calendar entry: a projected occurrence ('due') or a recorded run (its state tone). */
export interface DayEntry {
  readonly name: string;
  readonly time: string;
  readonly instant: string;
  readonly tone: 'due' | 'planned' | 'ok' | 'attention';
  readonly stateText: string;
  /** The routine this entry belongs to — what the agenda opens in the detail panel. */
  readonly scheduleId: string;
}

/** Place every schedule's projected occurrences + every recorded run onto its local day. */
export function entriesByDay(
  schedules: readonly ScheduleHealthScheduleRow[],
): ReadonlyMap<string, readonly DayEntry[]> {
  const byDay = new Map<string, DayEntry[]>();
  const add = (day: string, entry: DayEntry): void => {
    const list = byDay.get(day) ?? [];
    list.push(entry);
    byDay.set(day, list);
  };
  for (const row of schedules) {
    // An ARCHIVED routine lives under the list's Archived tab and nowhere else: no projected date (it has
    // none) and none of its past runs either — the calendar is the current board (owner ruling 2 Sep 2026).
    if (row.state === 'archived') continue;
    const projection = scheduleProjectionPresentation(row.state);
    for (const occurrence of row.nextOccurrences) {
      add(occurrence.localDate.slice(0, 10), {
        name: row.name,
        time: fmtClock(occurrence.instant),
        instant: occurrence.instant,
        tone: projection.tone,
        stateText: projection.entryStateText,
        scheduleId: row.scheduleId,
      });
    }
    for (const run of row.recentRuns) {
      const day = runLocalDate(run);
      if (day === null) continue;
      add(day, {
        name: row.name,
        time: fmtClock(run.startedAt),
        instant: run.startedAt,
        tone: run.state === 'failed' || run.state === 'interrupted' ? 'attention' : 'ok',
        stateText: runMarker(run),
        scheduleId: row.scheduleId,
      });
    }
  }
  for (const list of byDay.values()) list.sort((a, b) => (a.instant < b.instant ? -1 : 1));
  return byDay;
}
